import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './App.css';
import TradingChart from './components/TradingChart';
// import StatisticsChart from './components/StatisticsChart';
import Controls from './components/Controls';
import LoadingSkeleton from './components/LoadingSkeleton';

const API_BASE_URL = 'https://apibarbotine.xyz/api';

function App() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('BTC');
  const [selectedTimeRange, setSelectedTimeRange] = useState('1h');
  // const [statsPeriod, setStatsPeriod] = useState(20);

  // Normalize DWM values to fit within -2 to +2 range
  const normalizedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const dwmValues = data.map(item => item.dwm_value);
    const minDWM = Math.min(...dwmValues);
    const maxDWM = Math.max(...dwmValues);
    
    // Avoid division by zero
    if (minDWM === maxDWM) {
      return data.map(item => ({
        ...item,
        normalized_dwm: 0
      }));
    }
    
    // Normalize to -2 to +2 range
    return data.map(item => ({
      ...item,
      normalized_dwm: -2 + (4 * (item.dwm_value - minDWM) / (maxDWM - minDWM))
    }));
  }, [data]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const endTime = new Date();
      const startTime = new Date();
      
      // Parse custom time range
      const customTimeMatch = selectedTimeRange.match(/^(\d+)([mh])$/);
      
      if (customTimeMatch) {
        const [, value, unit] = customTimeMatch;
        const numValue = parseInt(value, 10);
        
        if (unit === 'm') {
          startTime.setMinutes(startTime.getMinutes() - numValue);
        } else if (unit === 'h') {
          startTime.setHours(startTime.getHours() - numValue);
        }
      } else {
        // Handle predefined ranges
        switch (selectedTimeRange) {
          case '10m':
            startTime.setMinutes(startTime.getMinutes() - 10);
            break;
          case '1h':
            startTime.setHours(startTime.getHours() - 1);
            break;
          case '4h':
            startTime.setHours(startTime.getHours() - 4);
            break;
          case '24h':
            startTime.setHours(startTime.getHours() - 24);
            break;
          default:
            startTime.setHours(startTime.getHours() - 1);
        }
      }

      const params = new URLSearchParams({
        start: startTime.toISOString(),
        end: endTime.toISOString(),
        limit: '1000',
        order: 'asc'
      });

      const response = await fetch(`${API_BASE_URL}/dwm/${selectedSymbol}?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result.data || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSymbol, selectedTimeRange]);

  // Fetch available symbols on component mount
  useEffect(() => {
    fetchSymbols();
  }, []);

  // Fetch data when symbol or time range changes
  useEffect(() => {
    if (selectedSymbol && selectedTimeRange) {
      fetchData();
    }
  }, [selectedSymbol, selectedTimeRange, fetchData]);

  const fetchSymbols = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dwm/`);
      const result = await response.json();
      setSymbols(result.symbols || []);
      if (result.symbols && result.symbols.length > 0) {
        setSelectedSymbol(result.symbols[0]);
      }
    } catch (err) {
      setError('Failed to fetch symbols');
      console.error('Error fetching symbols:', err);
    }
  };

  const handleSymbolChange = (symbol) => {
    setSelectedSymbol(symbol);
  };

  const handleTimeRangeChange = (range) => {
    setSelectedTimeRange(range);
  };

  const handleRefresh = () => {
    fetchData();
  };

  // const handlePeriodChange = (e) => {
  //   const value = parseInt(e.target.value, 10);
  //   if (!isNaN(value) && value > 0) {
  //     setStatsPeriod(value);
  //   }
  // };

  return (
    <div className="app">
      <div className="container">
        <header className="app-header">
          <h1>Delta-weighted-median (DWM)</h1>
          <a href='https://barbotine.xyz' target='_blank' rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '20px', color:'black', textDecoration: 'none', alignSelf: 'flex-center', justifySelf: 'center', justifyContent: 'center' }}>by <img alt='Barbotine logo' src='./logo.svg' height={30} /></a>
          <p style={{ marginTop:30, fontSize:14 }}>DWM is a high-frequency orderbook-based indicator developed by Barbotine to evaluate true market sentiment, especially on Bitfinex futures exchange. To understand how it works and how it's calculated, please refer to this <a href="https://barbotine.xyz/scalping-docs">link</a>.</p>
        </header>

        <Controls
          symbols={symbols}
          selectedSymbol={selectedSymbol}
          selectedTimeRange={selectedTimeRange}
          onSymbolChange={handleSymbolChange}
          onTimeRangeChange={handleTimeRangeChange}
          onRefresh={handleRefresh}
          loading={loading}
        />

        {error && (
          <div className="error-message">
            <p>Error: {error} Disable your ad-blocker or any extension that might interfere with browser's requests.</p>
            <button className="button secondary" onClick={handleRefresh}>
              Retry
            </button>
          </div>
        )}

        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <TradingChart data={normalizedData} symbol={selectedSymbol} />

            {/* <StatisticsChart 
              data={normalizedData} 
              symbol={selectedSymbol} 
              period={statsPeriod}
            /> */}
            
            {/* <div className="stats-controls card">
              <div className="stats-period">
                <label htmlFor="period-input">Rolling window size:</label>
                <input
                  id="period-input"
                  type="number"
                  min="2"
                  max="100"
                  value={statsPeriod}
                  onChange={handlePeriodChange}
                  className="select"
                />
                <span className="period-help">periods</span>
              </div>
            </div> */}
          </>
        )}
      </div>
    </div>
  );
}

export default App; 
